import React, { useRef, useState } from "react";
import { Card, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Define props interface
interface FileUploadProps {
  upload: (selectedFile: File) => Promise<void>;
  loading: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({ upload, loading }) => {
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Handle file drop
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFile = event.dataTransfer.files[0]; // Only take the first file
    setFile(droppedFile);
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]); // Replace with the new file
      event.target.value = "";
    }
  };

  // Trigger file input when "Browse Files" is clicked
  const handleBrowseClick = () => {
    if (file) {
      setFile(null);
    }
    fileInputRef.current?.click();
  };

  // Handle upload button click
  const handleUploadClick = async () => {
    if (!file) {
      toast.warn("Please select a file before uploading.");
      return;
    }
    try {
      await upload(file); // Pass the single selected file to the upload function
      setFile(null); // Clear file after successful upload
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Upload failed. Please try again.");
    }
  };

  return (
    <Card className="md-4 p-3 text-center">
      <Card.Body className="d-flex flex-column align-items-center justify-content-center">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="border border-primary rounded p-4"
          style={{
            cursor: "pointer",
            minHeight: "150px",
            background: "#f8f9fa",
            borderStyle: "dotted",
            width: "50%",
          }}
        >
          <p className="text-muted">Drag & Drop a file here</p>
          <p className="text-muted">or</p>
          <Button variant="secondary" onClick={handleBrowseClick}>
            Browse File
          </Button>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>

        {/* File Display */}
        {file && (
          <div className="mt-3">
            <h5>Selected File:</h5>
            <ul className="list-group">
              <li className="list-group-item">{file.name}</li>
            </ul>
          </div>
        )}

        {/* Upload Button */}
        <Button
          className="mt-3"
          variant="primary"
          onClick={handleUploadClick}
          disabled={!file || loading}
        >
          {loading ? "Searching..." : "Upload & Search"}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default FileUpload;
